import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { api } from "../lib/api";

/**
 * Tiny social-proof ticker showing the last few anonymized CAUL top-ups.
 * Fades through one row at a time so it never pulls focus from the CTAs.
 * Returns null if the backend has nothing to show.
 */
export default function CaulTopupTicker() {
  const [topups, setTopups] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/caul/topup/recent", { params: { limit: 5 } })
      .then(({ data }) => { if (!cancelled) setTopups(data.topups || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (topups.length <= 1) return;
    const id = setInterval(() => {
      setIdx((i) => (i + 1) % topups.length);
    }, 4000);
    return () => clearInterval(id);
  }, [topups.length]);

  if (topups.length === 0) return null;
  const row = topups[idx];

  return (
    <div
      className="mt-3 pt-3 border-t border-secondary/10 flex items-center gap-2 text-[12px] text-foreground/70"
      data-testid="caul-topup-ticker"
    >
      <Sparkles size={11} className="text-secondary/70 shrink-0" strokeWidth={2} />
      <span
        key={`${idx}-${row.caul}-${row.ago}`}
        className="lockup-pill-in inline-flex items-center gap-1.5 min-w-0 truncate"
        data-testid="caul-topup-ticker-row"
      >
        <b className="text-secondary/90 font-mono tracking-wider">{row.initials}</b>
        <span>topped up</span>
        <b className="text-foreground">{row.caul.toLocaleString()} CAUL</b>
        <span className="text-foreground/40">·</span>
        <span className="text-foreground/55">{row.ago}</span>
      </span>
      {topups.length > 1 && (
        <span
          className="ml-auto flex items-center gap-1 shrink-0"
          aria-label={`Recent top-up ${idx + 1} of ${topups.length}`}
          data-testid="caul-topup-ticker-dots"
        >
          {topups.map((_, i) => (
            <span
              key={i}
              className={`block w-1 h-1 rounded-full transition-colors ${i === idx ? "bg-secondary" : "bg-foreground/15"}`}
            />
          ))}
        </span>
      )}
    </div>
  );
}
